// src/components/vocabulary/VocabularyFlashcard.tsx
import React, { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaRandom,
  FaChevronLeft,
  FaChevronRight,
  FaStar,
} from "react-icons/fa";

interface VocabularyWord {
  wordId: number;
  germanWord: string;
  englishTranslation: string;
  bengaliTranslation: string;
  wordType: string;
  level: string;
  artikel?: string;
  pluralForm?: string;
  isRegularVerb?: boolean;
  oppositeWord?: string;
  meaningsEnglish: string | string[];
  meaningsBengali: string | string[];
  exampleGerman: string;
  exampleBengali: string;
  isLearned?: boolean;
  isMastered?: boolean;
}

interface Props {
  words: VocabularyWord[];
  onReview?: (wordId: number) => void;
  onMasterToggle?: (wordId: number, mastered: boolean) => void;
  isProductionMode?: boolean;
}

const WORDS_PER_PAGE = 20; // Show 20 words per page in list

const VocabularyFlashcard: React.FC<Props> = ({
  words,
  onReview,
  onMasterToggle,
  isProductionMode = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const currentWord = words[currentIndex];
  const totalPages = Math.ceil(words.length / WORDS_PER_PAGE);
  const startIndex = (currentPage - 1) * WORDS_PER_PAGE;
  const endIndex = Math.min(startIndex + WORDS_PER_PAGE, words.length);
  const paginatedWords = words.slice(startIndex, endIndex);

  useEffect(() => {
    updateProgress();
  }, [currentIndex, isFlipped, words.length]);

  useEffect(() => {
    // Update page when currentIndex changes
    const page = Math.floor(currentIndex / WORDS_PER_PAGE) + 1;
    setCurrentPage(page);
  }, [currentIndex]);

  const updateProgress = () => {
    const prog = currentIndex + (isFlipped ? 1 : 0);
    setProgress((prog / words.length) * 100);
  };

  const handleFlip = () => {
    setIsFlipped((prev) => !prev);
    if (!isFlipped && onReview && currentWord) {
      onReview(currentWord.wordId);
    }
  };

  const handleNext = () => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex((idx) => idx + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((idx) => idx - 1);
      setIsFlipped(false);
    }
  };

  const handleShuffle = () => {
    const randomIndex = Math.floor(Math.random() * words.length);
    setCurrentIndex(randomIndex);
    setIsFlipped(false);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Jump to first word of that page
      const newIndex = (page - 1) * WORDS_PER_PAGE;
      setCurrentIndex(newIndex);
      setIsFlipped(false);
    }
  };

  const getMeaningsArray = (meanings: string | string[]): string[] => {
    if (!meanings) return [];
    if (Array.isArray(meanings)) return meanings;
    if (typeof meanings === "string") {
      return meanings
        .split(",")
        .map((m) => m.trim())
        .filter((m) => m);
    }
    return [];
  };

  if (!currentWord) {
    return (
      <div className="text-center py-10 text-gray-600">No words available</div>
    );
  }

  const englishMeanings = getMeaningsArray(currentWord.meaningsEnglish);
  const bengaliMeanings = getMeaningsArray(currentWord.meaningsBengali);

  const handleMasterToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMasterToggle && currentWord) {
      onMasterToggle(currentWord.wordId, !currentWord.isMastered);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      {/* Production Mode Banner */}
      {isProductionMode && (
        <div className="mb-4 flex items-center justify-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-4 py-2 text-sm font-semibold">
          <span>🔄</span>
          <span>Runde 2 — Produktionsmodus: Errate das deutsche Wort!</span>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-4 sm:mb-6">
        <div className="bg-gray-200 rounded-full h-4 sm:h-5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#009fe3] to-[#0088cc] flex items-center justify-center text-white text-[11px] sm:text-xs font-semibold transition-all duration-300"
            style={{ width: `${progress}%` }}
          >
            {Math.round(progress)}%
          </div>
        </div>
        <p className="text-center mt-2 text-gray-600 text-xs sm:text-sm">
          Word {currentIndex + 1} of {words.length}
        </p>
      </div>

      {/* Flashcard */}
      <div
        className="relative w-full h-[340px] sm:h-[380px] cursor-pointer perspective-1000"
        onClick={handleFlip}
      >
        <div
          className={`absolute w-full h-full transition-transform duration-500 transform-style-preserve-3d ${
            isFlipped ? "rotate-y-180" : ""
          }`}
        >
          {/* Front */}
          <div className="absolute w-full h-full backface-hidden bg-white rounded-2xl shadow-xl p-6 sm:p-8 flex flex-col items-center justify-center border border-[#009fe3]/30">
            {isProductionMode ? (
              /* Production mode front: show English + Bengali, student guesses German */
              <>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#009fe3] mb-2 text-center">
                  {currentWord.englishTranslation}
                </div>
                <div
                  className="text-lg sm:text-xl text-gray-600 font-semibold mb-3 text-center"
                  style={{ fontFamily: "Noto Sans Bengali, sans-serif" }}
                >
                  {currentWord.bengaliTranslation}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#009fe3] text-white text-xs sm:text-sm font-semibold">
                    {currentWord.wordType}
                  </span>
                  {currentWord.level && (
                    <span className="px-2 py-1 rounded-full bg-blue-50 text-[#009fe3] text-[11px] font-semibold border border-[#009fe3]/30">
                      Level {currentWord.level}
                    </span>
                  )}
                </div>
                <p className="mt-4 text-gray-500 text-xs sm:text-sm">
                  Tap to reveal the German word
                </p>
              </>
            ) : (
              /* Normal mode front: show German word */
              <>
                <div className="text-3xl sm:text-4xl font-extrabold text-[#009fe3] mb-3 text-center">
                  {currentWord.germanWord}
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#009fe3] text-white text-xs sm:text-sm font-semibold">
                    {currentWord.wordType}
                  </span>
                  {currentWord.wordType === "VERB" && currentWord.isRegularVerb !== undefined && (
                    <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${currentWord.isRegularVerb ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                      {currentWord.isRegularVerb ? "regular verb" : "irregular verb"}
                    </span>
                  )}
                  {currentWord.level && (
                    <span className="px-2 py-1 rounded-full bg-blue-50 text-[#009fe3] text-[11px] font-semibold border border-[#009fe3]/30">
                      Level {currentWord.level}
                    </span>
                  )}
                </div>

                <div className="flex flex-col items-center gap-1 text-xs sm:text-sm text-gray-700">
                  {currentWord.wordType === "NOUN" && currentWord.artikel && (
                    <p>
                      <span className="font-semibold text-[#009fe3]">Artikel:</span>{" "}
                      <span className="font-semibold">{currentWord.artikel}</span>
                      {currentWord.pluralForm && (
                        <> · <span className="font-semibold text-[#009fe3]">Plural:</span>{" "}<span className="font-semibold">{currentWord.pluralForm}</span></>
                      )}
                    </p>
                  )}
                  {currentWord.wordType === "ADJECTIVE" && currentWord.oppositeWord && (
                    <p>
                      <span className="font-semibold text-[#009fe3]">Opposite:</span>{" "}
                      <span className="font-semibold">{currentWord.oppositeWord}</span>
                    </p>
                  )}
                </div>

                <p className="mt-5 text-gray-500 text-xs sm:text-sm">
                  Tap the card to see translations and examples
                </p>
              </>
            )}
          </div>

          {/* Back */}
          <div className="absolute w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-[#009fe3] to-[#0088cc] rounded-2xl shadow-xl overflow-hidden">
            <div className="h-full overflow-y-auto p-2 sm:p-6">
              {isProductionMode ? (
                /* Production mode back: reveal German word */
                <>
                  <div className="text-center mb-4 pb-3 border-b border-white/25">
                    <div className="text-3xl sm:text-4xl font-extrabold text-white mb-2">
                      {currentWord.germanWord}
                    </div>
                    {currentWord.wordType === "NOUN" && currentWord.artikel && (
                      <div className="text-yellow-200 text-sm font-semibold">
                        {currentWord.artikel} {currentWord.germanWord}
                        {currentWord.pluralForm && <span> · Pl: {currentWord.pluralForm}</span>}
                      </div>
                    )}
                  </div>
                  <div className="p-3 sm:p-4 bg-white/10 rounded-lg border border-white/20 mb-4">
                    <p className="font-semibold mb-2 text-yellow-200 text-sm sm:text-base">💬 Example</p>
                    <p className="text-white italic text-sm sm:text-base mb-2">"{currentWord.exampleGerman}"</p>
                    <p className="text-yellow-200 text-xs sm:text-sm" style={{ fontFamily: "Noto Sans Bengali, sans-serif" }}>"{currentWord.exampleBengali}"</p>
                  </div>
                </>
              ) : (
                /* Normal mode back: show translations */
                <>
                  <div className="text-center mb-4 pb-3 border-b border-white/25">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
                      {currentWord.englishTranslation}
                    </div>
                    <div className="text-lg sm:text-xl text-yellow-200 font-semibold" style={{ fontFamily: "Noto Sans Bengali, sans-serif" }}>
                      {currentWord.bengaliTranslation}
                    </div>
                  </div>

                  {englishMeanings.length > 0 && (
                    <div className="mb-4 p-3 sm:p-4 bg-white/10 rounded-lg border border-white/20">
                      <p className="font-semibold mb-2 text-yellow-200 text-sm sm:text-base">📖 Meanings</p>
                      <p className="text-sm sm:text-base text-white leading-relaxed">
                        {englishMeanings.map((meaning, idx) => (
                          <span key={idx}>
                            <span className="font-semibold">{meaning.trim()}</span>
                            {bengaliMeanings[idx] && (
                              <span className="text-yellow-200" style={{ fontFamily: "Noto Sans Bengali, sans-serif" }}>
                                {" "}({bengaliMeanings[idx].trim()})
                              </span>
                            )}
                            {idx < englishMeanings.length - 1 && ", "}
                          </span>
                        ))}
                      </p>
                    </div>
                  )}

                  <div className="p-3 sm:p-4 bg-white/10 rounded-lg border border-white/20">
                    <p className="font-semibold mb-2 text-yellow-200 text-sm sm:text-base">💬 Example</p>
                    <p className="text-white italic text-sm sm:text-base mb-2">"{currentWord.exampleGerman}"</p>
                    <p className="text-yellow-200 text-xs sm:text-sm" style={{ fontFamily: "Noto Sans Bengali, sans-serif" }}>"{currentWord.exampleBengali}"</p>
                  </div>
                </>
              )}

              {/* Mastered toggle — shown on back of card always */}
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleMasterToggle}
                  className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all shadow-md ${
                    currentWord.isMastered
                      ? "bg-yellow-400 text-yellow-900 hover:bg-yellow-300"
                      : "bg-white/20 text-white border border-white/40 hover:bg-white/30"
                  }`}
                >
                  <FaStar size={14} className={currentWord.isMastered ? "text-yellow-700" : "text-white/70"} />
                  {currentWord.isMastered ? "Gemeistert ✓" : "Als gemeistert markieren"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-gray-600 mt-3 sm:mt-4 text-xs sm:text-sm">
        Tap the card to flip · Use the controls below to navigate
      </p>

      {/* Controls */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mt-4 sm:mt-6">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
          }}
          disabled={currentIndex === 0}
          className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 
                     bg-gray-100 text-gray-800 rounded-lg text-sm sm:text-base font-medium
                     border border-gray-200 shadow-sm hover:bg-gray-200
                     disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <FaArrowLeft className="text-xs sm:text-sm" />
          <span>Previous</span>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleShuffle();
          }}
          className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5
                     bg-white text-[#009fe3] rounded-lg text-sm sm:text-base font-semibold
                     border border-[#009fe3] shadow-sm hover:bg-[#009fe3] hover:text-white
                     transition-all"
        >
          <FaRandom className="text-xs sm:text-sm" />
          <span>Shuffle</span>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          disabled={currentIndex === words.length - 1}
          className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2 sm:py-2.5 
                     bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white rounded-lg 
                     text-sm sm:text-base font-semibold shadow-md hover:shadow-lg
                     disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <span>Next</span>
          <FaArrowRight className="text-xs sm:text-sm" />
        </button>
      </div>

      {/* Word list with pagination */}
      <div className="mt-6 sm:mt-8 bg-white rounded-xl shadow-md p-4 sm:p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-[#009fe3] flex items-center gap-2">
            <span>📚</span> Vocabulary List
          </h3>
          {words.length > WORDS_PER_PAGE && (
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
          )}
        </div>

        {/* Word Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 mb-4">
          {paginatedWords.map((word, idx) => {
            const absoluteIndex = startIndex + idx;
            return (
              <button
                key={word.wordId}
                type="button"
                onClick={() => {
                  setCurrentIndex(absoluteIndex);
                  setIsFlipped(false);
                }}
                className={`text-left p-3 rounded-lg border-l-4 transition-all duration-150 ${
                  absoluteIndex === currentIndex
                    ? "bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white border-white shadow-md scale-[1.02]"
                    : "bg-gray-50 hover:bg-gray-100 border-[#009fe3] hover:border-[#0088cc] text-gray-900"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm sm:text-base truncate">
                      {word.germanWord}
                    </div>
                    <div
                      className={`text-xs sm:text-sm mt-1 truncate ${
                        absoluteIndex === currentIndex
                          ? "text-white/90"
                          : "text-gray-600"
                      }`}
                    >
                      {word.englishTranslation}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {word.isMastered && (
                      <FaStar size={11} className={absoluteIndex === currentIndex ? "text-yellow-300" : "text-yellow-500"} title="Gemeistert" />
                    )}
                    <span
                      className={`text-xs font-semibold ${
                        absoluteIndex === currentIndex
                          ? "text-white/80"
                          : "text-gray-400"
                      }`}
                    >
                      #{absoluteIndex + 1}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {words.length > WORDS_PER_PAGE && (
          <div className="flex flex-wrap items-center justify-center gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <FaChevronLeft />
            </button>

            {/* Page Numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                      currentPage === pageNum
                        ? "bg-[#009fe3] text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <FaChevronRight />
            </button>

            {/* Jump to page input for large lists */}
            {totalPages > 10 && (
              <div className="ml-4 flex items-center gap-2">
                <span className="text-sm text-gray-600">Go to:</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      handlePageChange(page);
                    }
                  }}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-[#009fe3] focus:border-[#009fe3]"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3D flip helpers */}
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-preserve-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        @media (hover: none) {
          button:active {
            transform: scale(0.97);
          }
        }
      `}</style>
    </div>
  );
};

export default VocabularyFlashcard;
